import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FaBriefcase, FaTrophy } from 'react-icons/fa';

const Experience = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const experiences = [
    {
      role: "Project Intern",
      organization: "National Dairy Development Board (NDDB)",
      duration: "Upcoming - Campus Placement",
      summary: "Selected through on-campus placement for dairy tech solutions development",
      status: "upcoming",
    },
    {
      role: "Training & Placement Coordinator",
      organization: "Birla Vishwakarma Mahavidyalaya (BVM)",
      duration: "Present",
      summary: "Campus placement coordination and student career mentoring",
      status: "current"
    },
    {
      role: "Project Intern",
      organization: "National Dairy Development Board (NDDB)",
      duration: "May 2025 – Jun 2025",
      summary: "Flutter-based dairy tech solutions",
      status: "completed"
    },
    {
      role: "Jr. Flutter App Developer",
      organization: "AMUL (GCMMF)",
      duration: "Dec 2024 – May 2025",
      summary: "Built Milk Cart & Sales Report apps",
      status: "completed"
    },
    {
      role: "Laravel Developer Intern",
      organization: "AMUL (GCMMF)",
      duration: "May 2024 – Jul 2024",
      summary: "Complaint Management System",
      status: "completed"
    },
    {
      role: "PHP Developer",
      organization: "Amul Dairy",
      duration: "Nov 2023",
      summary: "Employee & product management portal",
      status: "completed"
    },
    {
      role: "Intern (Angular + NestJS)",
      organization: "NXTHOP Solutions",
      duration: "Mar 2024 – Apr 2024",
      summary: "Employee Management System",
      status: "completed"
    },
    {
      role: "Data Science Intern",
      organization: "smartSense Consulting Solutions",
      duration: "Jun 2023 – Jul 2023",
      summary: "Insight-driven automation",
      status: "completed"
    },
    {
      role: ".NET Developer",
      organization: "Multipoint Infotech Solutions",
      duration: "Apr 2023 – Jul 2023",
      summary: "Backend and automation modules",
      status: "completed"
    }
  ];

  const leadership = [
    "Microsoft MVP (2025 – Present)",
    "Microsoft Learn Student Ambassador (2023 – 2025)",
    "IEEE Computer Society Vice Chairperson",
    "Training & Placement Coordinator – BVM",
    "Google Developer Student Clubs (GDSC) Core Member"
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { x: -50, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <section id="experience" className="section-padding">
      <div className="container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.6 }}
        >
          <div className="section-header">
            <h2 className="section-title">Experience & Leadership</h2>
            <div className="section-divider"></div>
            <p className="section-description">
              My professional journey and community contributions
            </p>
          </div>

          <div className="experience-layout">
            <div className="experience-column">
              <h3 className="subsection-title">
                <FaBriefcase /> Professional Experience
              </h3>
              <motion.div
                className="timeline"
                variants={containerVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
              >
                {experiences.map((exp, index) => (
                  <motion.div
                    key={index}
                    className="timeline-item"
                    variants={itemVariants}
                    initial={{ opacity: 0, x: -100, rotateY: -30 }}
                    animate={isInView ? { opacity: 1, x: 0, rotateY: 0 } : { opacity: 0, x: -100, rotateY: -30 }}
                    transition={{
                      type: "spring",
                      stiffness: 80,
                      damping: 15,
                      delay: index * 0.1
                    }}
                    whileHover={{ 
                      x: 15,
                      scale: 1.02,
                      boxShadow: "0 10px 30px rgba(99, 102, 241, 0.2)",
                      transition: { type: "spring", stiffness: 300, damping: 20 }
                    }}
                  >
                    <motion.div 
                      className={`timeline-dot ${exp.status === 'upcoming' ? 'upcoming' : exp.status === 'current' ? 'current' : ''}`}
                      animate={exp.status === 'current' || exp.status === 'upcoming' ? {
                        scale: [1, 1.3, 1],
                        boxShadow: [
                          "0 0 0 0 rgba(99, 102, 241, 0.4)",
                          "0 0 0 10px rgba(99, 102, 241, 0)",
                          "0 0 0 0 rgba(99, 102, 241, 0)"
                        ]
                      } : {}}
                      transition={{
                        repeat: Infinity,
                        duration: 2
                      }}
                    ></motion.div>
                    <div className={`timeline-content ${exp.status} ${exp.featured ? 'featured' : ''}`}>
                      <div className="timeline-header">
                        <motion.h4 
                          className="timeline-role"
                          initial={{ opacity: 0 }}
                          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                          transition={{ delay: index * 0.1 + 0.2 }}
                        >
                          {exp.role}
                        </motion.h4>
                        {exp.status === 'upcoming' && (
                          <motion.span 
                            className="status-badge upcoming"
                            animate={{
                              scale: [1, 1.1, 1],
                              boxShadow: [
                                "0 0 10px rgba(251, 146, 60, 0.3)",
                                "0 0 20px rgba(251, 146, 60, 0.5)",
                                "0 0 10px rgba(251, 146, 60, 0.3)"
                              ]
                            }}
                            transition={{
                              repeat: Infinity,
                              duration: 2
                            }}
                          >
                            Upcoming
                          </motion.span>
                        )}
                        {exp.status === 'current' && (
                          <motion.span 
                            className="status-badge current"
                            animate={{
                              scale: [1, 1.1, 1],
                              boxShadow: [
                                "0 0 10px rgba(34, 197, 94, 0.3)",
                                "0 0 20px rgba(34, 197, 94, 0.5)",
                                "0 0 10px rgba(34, 197, 94, 0.3)"
                              ]
                            }}
                            transition={{
                              repeat: Infinity,
                              duration: 2
                            }}
                          >
                            Current
                          </motion.span>
                        )}
                      </div>
                      <motion.p 
                        className="timeline-org"
                        initial={{ opacity: 0 }}
                        animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                        transition={{ delay: index * 0.1 + 0.3 }}
                      >
                        {exp.organization}
                      </motion.p>
                      <motion.p 
                        className="timeline-duration"
                        initial={{ opacity: 0 }}
                        animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                        transition={{ delay: index * 0.1 + 0.4 }}
                      >
                        {exp.duration}
                      </motion.p>
                      <motion.p 
                        className="timeline-summary"
                        initial={{ opacity: 0 }}
                        animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                        transition={{ delay: index * 0.1 + 0.5 }}
                      >
                        {exp.summary}
                      </motion.p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>

            <div className="leadership-column">
              <h3 className="subsection-title">
                <FaTrophy /> Leadership & Community
              </h3>
              <motion.div
                className="leadership-list"
                variants={containerVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
              >
                {leadership.map((role, index) => (
                  <motion.div
                    key={index}
                    className="leadership-item"
                    variants={itemVariants}
                    initial={{ scale: 0, rotate: -180 }}
                    animate={isInView ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -180 }}
                    transition={{
                      type: "spring",
                      stiffness: 120,
                      damping: 12,
                      delay: index * 0.1
                    }}
                    whileHover={{ 
                      scale: 1.08,
                      y: -5,
                      boxShadow: "0 10px 25px rgba(251, 191, 36, 0.3)",
                      transition: { 
                        type: "spring",
                        stiffness: 400,
                        damping: 10
                      }
                    }}
                  >
                    <motion.span 
                      className="leadership-icon"
                      animate={{
                        y: [0, -8, 0],
                        rotate: [0, 10, -10, 0]
                      }}
                      transition={{
                        repeat: Infinity,
                        duration: 3,
                        delay: index * 0.4
                      }}
                    >
                      🏆
                    </motion.span>
                    <span className="leadership-text">{role}</span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Experience;
